import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { notification, Spin } from "antd";
import dayjs from "dayjs";
import { FC, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getApiError,
    getTaskListsThunk,
    getWorkStartAndGetScheduleThunk,
    postGeoAdd,
    postWorkStopThunk,
} from "tm-shared/api";
import { UserRole, WorkStatus } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import { FloatingActionButton } from "@/components/UI/FloatingActionButton";
import { ROUTES } from "@/constants";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { Queue } from "@/hooks/useAsyncTaskQueue";
import { useQueueInfo } from "@/hooks/useQueueInfo";
import { ForceStopWorkModal } from "@/pages/WorkPlan/modals/IncompleteTasksModal";
import {
    getKpiGetMarketProgressesThunk,
    getPlansByMarketIdThunk,
    setIsInitialDataLoading,
} from "@/store/slices/offline";
import { selectUncompletedTasks } from "@/store/slices/progress";
import { addModalToQueue, setInitFailedRequests } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";
import { getPreciseLocation } from "@/utils/geo/geo";

import { useInitRetryRequests } from "./useInitRetryRequests";

interface WorkControlsProps {
    queue: Queue;
    openDayStartedModal: () => void;
}

export const WorkControls: FC<WorkControlsProps> = ({
    queue,
    openDayStartedModal,
}) => {
    const { id: userId, role, team } = useAppSelector((s) => s.user);
    const { offlineMode, isUnscheduledMarketsProcessing } = useAppSelector(
        (s) => s.offline,
    );

    const uncompletedTasks = useAppSelector(selectUncompletedTasks);
    const { workStatus, checkoutList } = useAppSelector((s) => s.work);
    const { activeSchedule } = useAppSelector((s) => s.sv);
    const {
        workPlan: { activeCollapse },
        initFailedRequests,
    } = useAppSelector((s) => s.ui);

    const [forceStopWorkModal, setForceStopWorkModal] = useState(false);
    const hasShownReminder = useRef<{ task: boolean; checkout: boolean }>({
        task: false,
        checkout: false,
    });

    const { hasPending } = useQueueInfo();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const initRequests = useInitRetryRequests();

    const manager = isManagerRole(role);

    const sendLocation = async () => {
        if (offlineMode) return;

        const locationTime = dayjs().toISOString();

        try {
            const { accuracy, lat, lng } = await getPreciseLocation();

            await postGeoAdd({
                accuracy,
                locationTime,
                latitude: lat,
                longitude: lng,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const [handleStartWork, isProcessing] = useAsyncAction(async () => {
        queue.clear();

        queue.add("Завантаження розкладу роботи", async () => {
            try {
                const { result } = await dispatch(
                    getWorkStartAndGetScheduleThunk({ userId }),
                ).unwrap();

                const marketIds = Object.keys(result?.markets ?? {});

                if (marketIds.length) {
                    await queue.add("Завантаження інформації про задачі", () =>
                        dispatch(
                            getTaskListsThunk({ targetIds: marketIds }),
                        ).unwrap(),
                    );

                    if (role === UserRole.Ta) {
                        await queue.add(
                            "Завантаження інформації про плани на ТТ",
                            async () => {
                                await dispatch(
                                    getPlansByMarketIdThunk({
                                        marketIds,
                                        routeId: team?.route?.uuid,
                                    }),
                                );

                                queue.add(
                                    "Завантаження інформації про факти на ТТ",
                                    () =>
                                        dispatch(
                                            getKpiGetMarketProgressesThunk({
                                                marketIds,
                                            }),
                                        ),
                                );
                            },
                        );
                    }
                }
            } catch (error) {
                const normalizeError = getApiError(error);

                if (normalizeError.code === "-32400") {
                    openDayStartedModal();
                    return;
                }

                throw error;
            }
        });

        initFailedRequests.forEach((type) => {
            const cmd = initRequests[type];
            if (!cmd) return;
            queue.add(cmd.title, cmd.run);
        });

        await queue.runParallel();
        dispatch(setIsInitialDataLoading(false));
        dispatch(setInitFailedRequests([]));
        sendLocation();
    });

    const [handleEndWork, isProcessingEndWork] = useAsyncAction(async () => {
        let isReadyToEndWork = true;

        if (uncompletedTasks.length > 0 && !hasShownReminder.current.task) {
            hasShownReminder.current.task = true;

            dispatch(
                addModalToQueue({
                    type: ModalType.UncompletedTasks,
                    id: ModalType.UncompletedTasks,
                }),
            );
        }

        const hasUncompletedCheckout = Object.values(checkoutList).length > 0;

        if (hasUncompletedCheckout && !hasShownReminder.current.checkout) {
            hasShownReminder.current.checkout = true;
            isReadyToEndWork = false;

            dispatch(
                addModalToQueue({
                    type: ModalType.UncompletedCheckouts,
                    id: ModalType.UncompletedCheckouts,
                }),
            );
        }

        if (!isReadyToEndWork) return;

        try {
            await dispatch(postWorkStopThunk({ force: false })).unwrap();
        } catch (error) {
            const { message, code } = getApiError(error);

            if (code === "-32500") {
                setForceStopWorkModal(true);
                return;
            }

            notification.error({
                message: "Не вдалося завершити робочий день",
                description: `${code}: ${message}`,
            });
        }

        sendLocation();
    });

    if (manager) {
        if (
            offlineMode ||
            isUnscheduledMarketsProcessing ||
            activeCollapse ||
            !activeSchedule
        ) {
            return null;
        }

        return (
            <FloatingActionButton
                onClick={() => navigate(ROUTES.WorkPlanUnplannedMarket)}
                icon={<PlusOutlined />}
                right={10}
                bottom={70}
                zIndex={1}
            />
        );
    }

    /**
     * Обычные пользователи
     */

    if (workStatus === WorkStatus.StartWork) {
        if (isProcessing) {
            return (
                <Spin
                    indicator={
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                    }
                />
            );
        }

        return (
            <>
                {!offlineMode &&
                    !isUnscheduledMarketsProcessing &&
                    !activeCollapse && (
                        <FloatingActionButton
                            onClick={() =>
                                navigate(ROUTES.WorkPlanUnplannedMarket)
                            }
                            icon={<PlusOutlined />}
                            right={10}
                            bottom={70}
                            zIndex={1}
                        />
                    )}

                {!activeCollapse && !offlineMode && (
                    <BaseButton
                        type="default"
                        danger
                        onClick={handleEndWork}
                        loading={isProcessingEndWork}
                        disabled={hasPending}
                    >
                        Завершити день
                    </BaseButton>
                )}

                {forceStopWorkModal && (
                    <ForceStopWorkModal
                        onClose={() => setForceStopWorkModal(false)}
                    />
                )}
            </>
        );
    }

    if (workStatus === WorkStatus.EndWork) {
        return (
            <BaseButton
                bgColor="success"
                type="primary"
                onClick={handleStartWork}
                loading={isProcessing}
            >
                Почати
            </BaseButton>
        );
    }

    return null;
};
